import 'reflect-metadata';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { ThemeProvider } from './context/ThemeProvider';
import { NetworkErrorProvider } from './context/NetworkErrorProvider';
import { FontProvider } from './context/FontProvider';
import { IconProvider } from './context/IconProvider';
import { ImageManagerProvider } from './context/ImageManagerProvider';
import { InspirationPagesProvider } from './context/InspirationPagesProvider';
import { NetworkStatusMonitor } from './components/NetworkStatusMonitor/NetworkStatusMonitor';
import { FullScreenErrorFallback } from './components/ErrorBoundary';
import { BuilderRouter } from '@/components/router';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './craftNodesLinked.css';
import 'intl-tel-input/build/css/intlTelInput.css';
import './index.css';
import { store } from './store/store';
import ToastContainer from './components/notifications/Toast/ToastContainer';
import { setupMixpanel } from './hooks/useMixpanel';
import { validateAuth } from './bootstrap';
import { setupHubSpot, isAdminLoggedInAsUser } from './hooks/useHubSpot';

// Potentially we'll just want this to stay for PROD only
/// Ticket: https://linear.app/leadpages/issue/ENG-18491/change-sentry-tracking-to-prod-only
if (import.meta.env.VITE_SENTRY_DSN_FRONTEND) {
  const enableReplay = import.meta.env.VITE_ENV === 'production';

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN_FRONTEND,
    environment: import.meta.env.VITE_ENV || 'production',
    // Release name must match what we set in vite.config.ts
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(enableReplay
        ? [
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            }),
          ]
        : []),
    ],
    tracesSampleRate: 0.1,
    ...(enableReplay && {
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
    }),
  });
}

// Skip analytics tracking when admin is logged in as another user
if (!isAdminLoggedInAsUser()) {
  setupMixpanel();
  setupHubSpot();
}

// Use Sentry's ErrorBoundary with our existing fallback component
const App = () => (
  <StrictMode>
    <BrowserRouter>
      <Sentry.ErrorBoundary fallback={<FullScreenErrorFallback />} showDialog={false}>
        <NetworkErrorProvider>
          <ThemeProvider>
            <Provider store={store}>
              <FontProvider>
                <IconProvider>
                  <ImageManagerProvider>
                    <InspirationPagesProvider>
                      <NetworkStatusMonitor>
                        <ToastContainer />
                        <BuilderRouter />
                      </NetworkStatusMonitor>
                    </InspirationPagesProvider>
                  </ImageManagerProvider>
                </IconProvider>
              </FontProvider>
            </Provider>
          </ThemeProvider>
        </NetworkErrorProvider>
      </Sentry.ErrorBoundary>
    </BrowserRouter>
  </StrictMode>
);

const startApp = async () => {
  const isAuthenticated = await validateAuth();

  if (!isAuthenticated) {
    // Avoid render flashes while redirect could be occurring.
    return;
  }

  const rootElement = document.getElementById('root');

  if (rootElement) {
    createRoot(rootElement).render(<App />);
  } else {
    console.error('Root element not found');
  }
};

startApp();
